import { useParams, Link, Navigate } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { OFFERINGS, OFFER_TIERS, COMPANY } from "../lib/content";
import { OFFERINGS_CONTENT } from "../lib/offerings_content";
import { useSaved } from "../lib/useSaved";
import { Reveal, MaskReveal } from "../components/Reveal";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowUpRight, Lock, Check, Download,
  Bookmark, BookmarkCheck, ShieldCheck, FileText, X, Loader2, Sparkles
} from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

function formatPrice(p) {
  if (p === 0) return "Free";
  if (Number.isInteger(p) && p < 10) return `$${p}.00`;
  return `$${p.toLocaleString()}`;
}

function Section({ s, sectionId }) {
  return (
    <section id={sectionId} className="mt-14 md:mt-20 scroll-mt-28">
      <Reveal>
        <div className="overline text-ink/40 mb-1">§</div>
        <h2 className="font-display uppercase text-3xl md:text-5xl leading-[0.9] tracking-tight">{s.h}</h2>
      </Reveal>
      {s.body && (
        <div className="mt-6 space-y-5">
          {s.body.map((p, i) => (
            <Reveal key={i} delay={0.04 * i}><p className="font-serif text-lg md:text-xl leading-[1.7] text-ink/85">{p}</p></Reveal>
          ))}
        </div>
      )}
      {s.list && (
        <ol className="mt-6 space-y-3 list-none">
          {s.list.map((item, i) => {
            const text = typeof item === "string" ? item : `${item.term} — ${item.def}`;
            return (
              <Reveal key={i} delay={0.02 * i}>
                <li className="group font-serif text-lg md:text-xl leading-snug text-ink/85 pl-6 border-l border-ink/15 hover:border-ink transition-colors">
                  {text}
                </li>
              </Reveal>
            );
          })}
        </ol>
      )}
    </section>
  );
}

function BuyerModal({ open, onClose, onSubmit, offer, editionNo, busy }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [brand, setBrand] = useState("");
  useEffect(() => { if (!open) { setName(""); setEmail(""); setBrand(""); } }, [open]);
  if (!open) return null;
  const free = offer.price === 0;
  const coaching = offer.kind === "coaching";
  return (
    <div className="fixed inset-0 z-50 bg-ink/70 backdrop-blur-sm flex items-center justify-center p-6 no-print" data-testid="buyer-modal">
      <div className="bg-cream max-w-lg w-full p-8 md:p-10 relative border border-ink/20">
        <button onClick={onClose} className="absolute top-4 right-4 text-ink/50 hover:text-ink" data-testid="modal-close" aria-label="Close">
          <X size={18} />
        </button>
        <div className="overline text-ink/55">
          {coaching ? "Coaching · 60 min with Meghan" : `Edition No. ${editionNo} · ${formatPrice(offer.price)}`}
        </div>
        <h3 className="font-display uppercase text-3xl md:text-4xl mt-3 leading-[0.95]">
          {coaching ? "Request your call." : free ? "One quick detail." : "Get instant access."}
        </h3>
        <p className="font-serif italic text-ink/70 text-lg mt-3 leading-snug">
          {coaching
            ? "Tell us who you are. Meghan personally confirms within one business day with a Zoom link, intake brief, and your written prep notes."
            : free
              ? "Enter your details — your edition unlocks instantly here in your browser. No waiting on email."
              : "Enter your details — the full edition downloads instantly here in your browser. Your receipt is sent automatically."}
        </p>
        <form
          onSubmit={(e) => { e.preventDefault(); if (!name || !email) return; onSubmit({ name, email, brand: brand || undefined }); }}
          className="mt-7 space-y-4"
        >
          <div>
            <label className="overline text-ink/55 block mb-1">Your name</label>
            <input
              type="text" required value={name} onChange={(e) => setName(e.target.value)}
              className="w-full bg-transparent border border-ink/20 focus:border-ink outline-none px-3 py-2.5 font-serif text-base text-ink"
              data-testid="buyer-name" placeholder="Meghan Amaral"
            />
          </div>
          <div>
            <label className="overline text-ink/55 block mb-1">Email</label>
            <input
              type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-transparent border border-ink/20 focus:border-ink outline-none px-3 py-2.5 font-serif text-base text-ink"
              data-testid="buyer-email" placeholder="you@brand.com"
            />
          </div>
          <div>
            <label className="overline text-ink/55 block mb-1">
              {coaching ? "Brand / Company + topic you want to cover" : "Brand / Company"} <span className="text-ink/35">(optional)</span>
            </label>
            <input
              type="text" value={brand} onChange={(e) => setBrand(e.target.value)}
              className="w-full bg-transparent border border-ink/20 focus:border-ink outline-none px-3 py-2.5 font-serif text-base text-ink"
              data-testid="buyer-brand" placeholder={coaching ? "Atelier Soleil — positioning + first press" : "M Media Corp"}
            />
          </div>
          <button
            type="submit" disabled={busy}
            className="btn-ink bg-ink text-cream border-ink hover:bg-transparent hover:text-ink w-full justify-center"
            data-testid="buyer-submit"
          >
            {busy
              ? <><Loader2 size={14} className="animate-spin" /> {coaching ? "Sending your request…" : "Preparing your edition…"}</>
              : coaching
                ? <><Sparkles size={14} /> Request the call — {formatPrice(offer.price)}</>
                : <><Download size={14} /> {free ? "Send & download — Free" : `Send & download — ${formatPrice(offer.price)}`}</>
            }
          </button>
          <p className="overline text-ink/40 text-center">
            {coaching ? "Confirmed manually by Meghan within one business day." : "10% of every download routes to our charity partners."}
          </p>
        </form>
      </div>
    </div>
  );
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; document.body.appendChild(a); a.click();
  setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 250);
}

export default function StudioItem() {
  const { id } = useParams();
  const offer = OFFERINGS.find((o) => o.id === id);
  const content = OFFERINGS_CONTENT[id];
  const tier = OFFER_TIERS.find((t) => t.id === offer?.tier);
  const isFree = offer ? offer.price === 0 : true;
  const isCoaching = offer?.kind === "coaching";
  const { isSaved, toggle: toggleSaved } = useSaved();

  const [scrollPct, setScrollPct] = useState(0);
  const [showSticky, setShowSticky] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const buyBtnRef = useRef(null);

  const editionNo = useMemo(() => {
    const idx = OFFERINGS.findIndex((o) => o.id === id);
    return idx >= 0 ? String(idx + 1).padStart(3, "0") : "—";
  }, [id]);

  const related = useMemo(() => {
    if (!offer) return [];
    return OFFERINGS.filter((o) => o.tier === offer.tier && o.id !== offer.id).slice(0, 3);
  }, [offer]);

  useEffect(() => { window.scrollTo(0, 0); }, [id]);
  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement;
      setScrollPct(Math.min(100, Math.max(0, (h.scrollTop / (h.scrollHeight - h.clientHeight)) * 100)));
      setShowSticky(h.scrollTop > 600);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (!offer || !content) return <Navigate to="/studio" replace />;

  const requestPdf = async (buyer) => {
    setDownloading(true);
    try {
      const payload = {
        title: offer.title,
        subtitle: content.subtitle || "",
        byline: content.byline || "",
        category: offer.category,
        tier_label: tier?.label || "Studio",
        edition_no: editionNo,
        price: offer.price,
        price_label: formatPrice(offer.price),
        is_free: isFree,
        what_youll_get: content.what_youll_get || [],
        modules: content.modules || null,
        sections: (content.sections || []).map((s) => ({
          h: s.h, body: s.body || null,
          list: (s.list || []).map((it) => typeof it === "string" ? it : { term: it.term, def: it.def }),
        })),
        buyer: buyer || null,
      };
      const res = await fetch(`${API}/studio/${id}/pdf`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(`PDF request failed: ${res.status} — ${t.slice(0, 100)}`);
      }
      const blob = await res.blob();
      const filename = `mmedia-edition-${editionNo}-${offer.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40)}.pdf`;
      downloadBlob(blob, filename);
      toast.success(isFree ? "Edition downloaded. Enjoy." : "Edition delivered — receipt on its way to your inbox.");
      setModalOpen(false);
    } catch (e) {
      toast.error("Sorry — something went wrong. Please try again or email studio@mmediacorp.com.");
      console.error(e);
    } finally {
      setDownloading(false);
    }
  };

  const onDownload = () => {
    // Email capture is required for BOTH free and paid editions (and coaching)
    setModalOpen(true);
  };

  // Submit handler for the modal — branches on coaching vs PDF download
  const onModalSubmit = async (buyer) => {
    if (isCoaching) {
      setDownloading(true);
      try {
        const res = await fetch(`${API}/studio/order`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            item_id: offer.id,
            name: buyer.name,
            email: buyer.email,
            brand_url: buyer.brand || null,
            amount: offer.price,
            kind: "coaching",
            notes: buyer.brand || null,
          }),
        });
        if (!res.ok) {
          const txt = await res.text().catch(() => "");
          throw new Error(`Booking request failed: ${res.status} ${txt.slice(0, 120)}`);
        }
        toast.success("Booking received. Meghan will email you within one business day with a Zoom link.");
        setModalOpen(false);
        setTimeout(() => {
          const el = document.getElementById("coaching-confirmation");
          el?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 200);
      } catch (e) {
        toast.error("Sorry — we couldn't save your booking. Please email studio@mmediacorp.com directly.");
        console.error(e);
      } finally {
        setDownloading(false);
      }
      return;
    }
    return requestPdf(buyer);
  };

  return (
    <article className="pt-24 md:pt-32 pb-24 bg-cream relative" data-testid="studio-item">
      <style>{PRINT_AND_PROGRESS_CSS}</style>

      {/* reading progress bar */}
      <div className="fixed top-0 left-0 right-0 z-50 h-[2px] bg-ink/10 no-print">
        <div className="h-full bg-ink transition-[width] duration-150" style={{ width: `${scrollPct}%` }} />
      </div>

      <div className="container-edge no-print pt-4 flex items-center justify-between">
        <Link to="/studio" className="overline text-ink/55 hover:text-ink inline-flex items-center gap-2" data-testid="back-to-studio">
          <ArrowLeft size={12} /> Back to the Studio
        </Link>
        <button
          onClick={() => toggleSaved(id)}
          className={`inline-flex items-center gap-2 overline transition-colors ${isSaved(id) ? "text-ink" : "text-ink/45 hover:text-ink"}`}
          data-testid="save-toggle"
        >
          {isSaved(id) ? <><BookmarkCheck size={13} /> Saved</> : <><Bookmark size={13} /> Save for later</>}
        </button>
      </div>

      {/* COVER */}
      <header className="container-edge mt-10">
        <div className="grid grid-cols-12 gap-y-10 gap-x-8 border-y border-ink/20 py-10 md:py-14">
          <div className="col-span-12 md:col-span-3 order-2 md:order-1">
            <div className="border border-ink/20 p-5" data-testid="edition-seal">
              <div className="overline text-ink/60">M Media Corp · The Studio</div>
              <div className="font-display text-7xl leading-none mt-3 tracking-tight">No.<br/>{editionNo}</div>
              <div className="mt-4 border-t border-ink/15 pt-4">
                <div className="overline text-ink/55">{tier?.label}</div>
                <div className="overline text-ink/45 mt-1">Edition · 2026</div>
              </div>
              <div className="mt-5 flex items-center gap-2 overline text-ink/45">
                <ShieldCheck size={11} /> Verified by the desk
              </div>
            </div>
          </div>
          <div className="col-span-12 md:col-span-9 order-1 md:order-2">
            <div className="flex items-baseline justify-between flex-wrap gap-4 mb-3">
              <div className="overline text-ink/55">{offer.category} · {tier?.label}</div>
              <div className="font-display text-3xl md:text-4xl tracking-tight" data-testid="item-price">{formatPrice(offer.price)}</div>
            </div>
            <MaskReveal delay={0.05}>
              <h1 className="font-display uppercase text-5xl md:text-[7vw] leading-[0.86]">{offer.title}</h1>
            </MaskReveal>
            <p className="font-serif italic text-xl md:text-3xl text-ink/80 mt-7 max-w-4xl leading-snug">{content.subtitle}</p>
            <div className="overline text-ink/55 mt-6">{content.byline}</div>

            <div className="mt-9 flex flex-wrap items-center gap-3 no-print" data-testid="item-ctas">
              <button
                ref={buyBtnRef}
                onClick={onDownload}
                disabled={downloading}
                className="btn-ink bg-ink text-cream border-ink hover:bg-transparent hover:text-ink"
                data-testid={isCoaching ? "coaching-book-cta" : isFree ? "free-download-cta" : "paid-buy-cta"}
              >
                {downloading
                  ? <><Loader2 size={14} className="animate-spin" /> Preparing…</>
                  : isCoaching
                    ? <><Sparkles size={14} /> Book the call — {formatPrice(offer.price)}</>
                    : isFree
                      ? <><Download size={14} /> Download — Free</>
                      : <><Sparkles size={14} /> Get instant access — {formatPrice(offer.price)}</>
                }
              </button>
              {!isCoaching && (
                <a href="#document" className="btn-ghost" data-testid="read-cta">
                  <FileText size={14} /> Read the document
                </a>
              )}
              <span className="overline text-ink/45">{isCoaching ? "60 min with Meghan · written action plan included" : "Email-gated · instant delivery"}</span>
            </div>

            <div className="mt-9 grid grid-cols-2 md:grid-cols-4 gap-y-3 gap-x-6 border-t border-ink/15 pt-6 text-sm">
              <div className="flex items-center gap-2"><ShieldCheck size={14} className="text-ink/70" /><span className="overline text-ink/65">10% to charity</span></div>
              <div className="flex items-center gap-2"><Download size={14} className="text-ink/70" /><span className="overline text-ink/65">Instant delivery</span></div>
              <div className="flex items-center gap-2"><Bookmark size={14} className="text-ink/70" /><span className="overline text-ink/65">Lifetime access</span></div>
              <div className="flex items-center gap-2"><FileText size={14} className="text-ink/70" /><span className="overline text-ink/65">License: 1 brand</span></div>
            </div>
          </div>
        </div>
      </header>

      {/* WHAT YOU'LL GET */}
      <section className="container-edge mt-16 md:mt-24 grid grid-cols-12 gap-8" data-testid="what-youll-get">
        <div className="col-span-12 md:col-span-4">
          <div className="overline text-ink/55">Inside the edition</div>
          <h3 className="font-display uppercase text-4xl md:text-5xl mt-3 leading-[0.9]">What you&apos;ll get.</h3>
        </div>
        <ul className="col-span-12 md:col-span-7 md:col-start-6 space-y-4">
          {content.what_youll_get.map((it, i) => (
            <Reveal key={i} delay={i * 0.04}>
              <li className="flex items-start gap-4 border-t border-ink/15 pt-4">
                <Check size={16} className="mt-1 shrink-0 text-ink/70" />
                <span className="font-serif text-lg md:text-xl text-ink/85 leading-snug">{it}</span>
              </li>
            </Reveal>
          ))}
        </ul>
      </section>

      {/* CURRICULUM */}
      {content.modules?.length > 0 && (
        <section className="container-edge mt-16 md:mt-24 grid grid-cols-12 gap-8" data-testid="modules">
          <div className="col-span-12 md:col-span-4">
            <div className="overline text-ink/55">Curriculum</div>
            <h3 className="font-display uppercase text-4xl md:text-5xl mt-3 leading-[0.9]">The volumes.</h3>
          </div>
          <ol className="col-span-12 md:col-span-7 md:col-start-6 space-y-4">
            {content.modules.map((m, i) => (
              <Reveal key={i} delay={i * 0.03}>
                <li className="flex items-baseline gap-5 border-t border-ink/15 pt-4">
                  <span className="overline text-ink/45 shrink-0 w-10 font-display text-xl">{String(i + 1).padStart(2, "0")}</span>
                  <span className="font-serif text-lg md:text-xl text-ink/85 leading-snug">{m}</span>
                </li>
              </Reveal>
            ))}
          </ol>
        </section>
      )}

      {/* CONTENT */}
      <section id={isFree ? "document" : "preview"} className="container-edge mt-20 md:mt-28" data-testid="content-body">
        <div className="grid grid-cols-12 gap-8">
          <aside className="col-span-12 md:col-span-3 md:sticky md:top-32 md:self-start no-print">
            <div className="overline text-ink/55">{isFree ? "The Document" : "The Preview"}</div>
            <p className="font-serif italic text-base text-ink/65 mt-3 leading-snug">
              {isFree ? "Everything below is yours — read it, save it, share with a peer." : "A working sample. Get full access to unlock the rest."}
            </p>
            {(content.sections || []).length > 1 && (
              <nav className="mt-6 border-t border-ink/15 pt-5" data-testid="toc">
                <div className="overline text-ink/45 mb-3">Contents</div>
                <ol className="space-y-2.5">
                  {(content.sections || []).map((s, i) => (
                    <li key={i} className="flex items-baseline gap-3">
                      <span className="overline text-ink/35 w-6 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                      <a href={`#sec-${i}`} className="font-serif italic text-ink/75 hover:text-ink hover:underline decoration-1 underline-offset-4 transition-colors text-base leading-snug">
                        {s.h}
                      </a>
                    </li>
                  ))}
                </ol>
              </nav>
            )}
          </aside>

          <div className="col-span-12 md:col-span-8 md:col-start-5 max-w-3xl prose-editorial">
            {(content.sections || []).map((s, i) => (<Section key={i} s={s} sectionId={`sec-${i}`} />))}

            {!isFree && content.sample_locked && (
              <div className="mt-20 relative no-print" data-testid="paywall">
                <div className="absolute -top-24 left-0 right-0 h-24 bg-gradient-to-b from-transparent to-cream pointer-events-none" aria-hidden />
                <div className="border border-ink/25 bg-ink/[0.04] p-8 md:p-12">
                  <div className="flex items-center gap-3"><Lock size={20} className="text-ink/60" /><span className="overline text-ink/55">Members beyond this point</span></div>
                  <h3 className="font-display uppercase text-3xl md:text-5xl mt-4 leading-[0.95]">
                    Unlock Edition No. {editionNo}<br/>
                    <span className="serif-italic font-serif normal-case text-ink/75 text-2xl md:text-3xl">— the full {tier?.label.toLowerCase()}.</span>
                  </h3>
                  <p className="font-serif text-lg md:text-xl text-ink/75 mt-5 max-w-2xl leading-snug">
                    {content.modules?.length || 5} volumes, the full document, every template & swipe file — generated and delivered the moment you confirm.
                  </p>
                  <ul className="mt-7 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 text-base">
                    <li className="flex items-center gap-3 text-ink/85"><Check size={14} /> Lifetime access · future updates</li>
                    <li className="flex items-center gap-3 text-ink/85"><Check size={14} /> Branded PDF, ready to print</li>
                    <li className="flex items-center gap-3 text-ink/85"><Check size={14} /> Single-brand commercial licence</li>
                    <li className="flex items-center gap-3 text-ink/85"><Check size={14} /> 10% to charity partners</li>
                  </ul>
                  <div className="mt-8 flex flex-wrap gap-4 items-center">
                    <button onClick={() => setModalOpen(true)} disabled={downloading} className="btn-ink bg-ink text-cream border-ink hover:bg-transparent hover:text-ink" data-testid="paywall-cta">
                      <Sparkles size={14} /> Get instant access — {formatPrice(offer.price)} <ArrowUpRight size={14} />
                    </button>
                    <Link to="/apply" className="btn-ghost">Or apply for the partnership</Link>
                  </div>
                  <blockquote className="mt-10 border-l-2 border-ink/20 pl-5 font-serif italic text-ink/80 text-lg leading-snug max-w-2xl">
                    &ldquo;The single most useful document on my desk this year — and the only one we&apos;ve quoted in board meetings.&rdquo;
                    <footer className="overline text-ink/50 mt-2 not-italic">— Founder, M Media Studio reader</footer>
                  </blockquote>
                </div>
              </div>
            )}

            {isFree && (
              <div className="mt-20 border-t border-ink/15 pt-8 no-print">
                <div className="flex items-baseline justify-between flex-wrap gap-3 mb-5">
                  <div className="overline text-ink/45">— End of Edition No. {editionNo} —</div>
                  <div className="overline text-ink/45">{COMPANY.name} · The Studio</div>
                </div>
                <p className="font-serif italic text-ink/65 text-lg">
                  Published by {COMPANY.name} as a gift to founders. Reproduction with attribution permitted. Commercial resale is not.
                </p>
                <div className="mt-7 flex flex-wrap gap-4">
                  <button onClick={onDownload} disabled={downloading} className="btn-ink bg-ink text-cream border-ink hover:bg-transparent hover:text-ink" data-testid="free-download-cta-bottom">
                    {downloading ? <><Loader2 size={14} className="animate-spin" /> Preparing…</> : <><Download size={14} /> Download branded PDF</>}
                  </button>
                  <Link to="/studio" className="btn-ghost">Browse more editions →</Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* RELATED */}
      {related.length > 0 && (
        <section className="container-edge mt-28 md:mt-36 no-print" data-testid="related">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="overline text-ink/55">From the same tier</div>
              <h3 className="font-display uppercase text-4xl md:text-6xl mt-3 leading-[0.9]">Other {tier?.label.toLowerCase()}.</h3>
            </div>
            <Link to="/studio" className="btn-ghost">All 100 editions <ArrowUpRight size={14} /></Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-y-10 gap-x-10">
            {related.map((r, i) => (
              <Reveal key={r.id} delay={i * 0.06}>
                <Link to={`/studio/${r.id}`} className="group block border-t border-ink/20 pt-5" data-cursor="hover" data-testid={`related-${r.id}`}>
                  <div className="flex items-baseline justify-between mb-3">
                    <span className="overline text-ink/55">{r.category}</span>
                    <span className="overline text-ink">{formatPrice(r.price)}</span>
                  </div>
                  <h4 className="font-serif text-2xl md:text-3xl leading-snug group-hover:underline decoration-1 underline-offset-4">{r.title}</h4>
                  <p className="text-ink/65 mt-3 leading-relaxed">{r.promise}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* STICKY BUY BAR */}
      {!isFree && showSticky && (
        <div className="fixed bottom-4 left-4 right-4 md:left-1/2 md:right-auto md:-translate-x-1/2 md:bottom-6 z-40 no-print" data-testid="sticky-buy">
          <div className="bg-ink text-cream shadow-2xl flex items-center justify-between gap-4 px-5 py-3 md:px-7 md:py-4 max-w-3xl mx-auto">
            <div className="hidden md:block">
              <div className="overline text-cream/55">Edition No. {editionNo}</div>
              <div className="font-serif text-base md:text-lg truncate max-w-md">{offer.title}</div>
            </div>
            <button onClick={() => setModalOpen(true)} className="btn-ink bg-cream text-ink border-cream hover:bg-transparent hover:text-cream" data-testid="sticky-buy-cta">
              <Sparkles size={14} /> Get access — {formatPrice(offer.price)}
            </button>
          </div>
        </div>
      )}

      <BuyerModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={onModalSubmit}
        offer={offer}
        editionNo={editionNo}
        busy={downloading}
      />
    </article>
  );
}

const PRINT_AND_PROGRESS_CSS = `
@media print {
  .no-print { display: none !important; }
  body, html { background: #fff !important; color: #000 !important; }
  article { padding-top: 0 !important; }
  .container-edge { padding-left: 24px !important; padding-right: 24px !important; max-width: none !important; }
  h1 { font-size: 36pt !important; line-height: 1.0 !important; }
  h2 { font-size: 18pt !important; margin-top: 18pt !important; }
  p, li { font-size: 11.5pt !important; line-height: 1.55 !important; color: #000 !important; }
  .font-serif { font-family: 'Cormorant Garamond', Georgia, serif !important; }
  .font-display { font-family: 'Bebas Neue', Helvetica, sans-serif !important; }
  section { page-break-inside: avoid; }
  aside { display: none !important; }
}
`;
